import React, { useEffect } from 'react'
import Chart from 'react-google-charts';
import { GoogleChartWrapper } from 'react-google-charts/dist/types';
import { CHART_DEFAULTS, DataSource, GenerateChartsProps } from '../common';
import { hasConsistentResolvedUnits, resolveUnitData } from '../../common';
import { getUnit } from '../../questionView';
import { toastFromError } from '@g17eco/molecules/toasts';
import { loggerMessage } from '../../../../logger';
import { QUESTION } from '@constants/terminology';

const WIDTH = 600;
const HEIGHT = 400;

export interface GHGEmissionsDataSource extends DataSource {
  totalEmissions?: number;
  unitLabel?: string;
  decimal?: number;
}

const GHGEmissions = React.memo((props: GenerateChartsProps) => {
  const { questionData, updateData, displayUserInput } = props;

  const scope1Utr = questionData.find(d => d.universalTracker.code === 'gri/2020/305-1/a');
  const scope2aUtr = questionData.find(d => d.universalTracker.code === 'gri/2020/305-2/a');
  const scope2bUtr = questionData.find(d => d.universalTracker.code === 'gri/2020/305-2/b');
  const scope3Utr = questionData.find(d => d.universalTracker.code === 'gri/2020/305-3/a');

  const scope1Data = resolveUnitData(scope1Utr, displayUserInput);
  const scope2aData = resolveUnitData(scope2aUtr, displayUserInput);
  const scope2bData = resolveUnitData(scope2bUtr, displayUserInput);
  const scope3Data = resolveUnitData(scope3Utr, displayUserInput);

  const allResolved = [scope1Data, scope2aData, scope2bData, scope3Data];
  const isMismatched = !hasConsistentResolvedUnits(allResolved);

  useEffect(() => {
    if (isMismatched) {
      toastFromError({ error: new Error(`GHG scope ${QUESTION.PLURAL} have inconsistent units. Charts and data will be skipped.`), title: 'GHG Emissions' });
      loggerMessage('GHG emissions unit mismatch in SDG Impact Report', {
        level: 'error',
        resolvedUnits: allResolved.map(s => s.unit),
        resolvedNumberScales: allResolved.map(s => s.numberScale),
      });
      updateData('ghgEmissions', { loaded: true });
    }
  }, [isMismatched]);

  if (isMismatched) {
    return null;
  }

  const scope1 = scope1Data.value;
  const scope2 = scope2aData.value + scope2bData.value;
  const scope3 = scope3Data.value;

  const unitLabel = scope1Utr ? getUnit(scope1Utr, undefined, displayUserInput) : undefined;

  const reportData: GHGEmissionsDataSource = {
    loaded: true,
    width: WIDTH,
    height: HEIGHT,
    totalEmissions: isMismatched ? undefined : scope1 + scope2 + scope3,
    unitLabel,
    decimal: scope1Utr?.universalTracker.valueValidation?.decimal,
  }

  const chartData = [
    ['Scope', 'Emissions', { role: 'style' }],
    ['Scope 1', scope1, '#A6CFE5'],
    ['Scope 2', scope2, '#D6F5DF'],
    ['Scope 3', scope3, '#1A5A87'],
  ];

  return <Chart
    chartType='ColumnChart'
    data={chartData}
    width={WIDTH}
    height={HEIGHT}
    options={{
      ...CHART_DEFAULTS.OPTIONS,
      title: 'Scope 1, Scope 2, and Scope 3 emissions.',
      chartArea: {
        top: 40,
        bottom: 20,
        left: 100,
        right: 20,
      },
      legend: {
        position: 'none'
      },
    }}
    chartEvents={[
      {
        eventName: 'ready',
        callback: (ChartRef: { chartWrapper: GoogleChartWrapper }) => {
          reportData.chart = (ChartRef.chartWrapper.getChart().getImageURI() as unknown as string).split(',')[1];
          updateData('ghgEmissions', reportData);
        }
      }
    ]}
  />
})

export default GHGEmissions;
